import { FamilyMember } from "./familyTreeUtils";
import { calculateEditStatus } from "./familyTreeProgressUtils";
import { getParents } from "./familyTreeMigrationUtils";

/**
 * Guided-entry support for the family tree builder: flattens the ancestor
 * tree into an ordered, labeled checklist (You → Parents → Grandparents → …)
 * so customers can fill people in sequentially instead of hunting for nodes
 * on the canvas.
 */

export interface GuidedEntryItem {
  member: FamilyMember;
  /** e.g. "You", "Father", "Paternal Grandmother", "Spouse's Father" */
  relationshipLabel: string;
  /** Name (or label) of the person this member is a parent of, if any */
  parentOfLabel: string | null;
  /** 0 = you/spouse, 1 = parents, 2 = grandparents, … */
  generation: number;
  status: "incomplete" | "partial" | "complete";
}

const GENERATION_NAMES = [
  "You & Spouse",
  "Parents",
  "Grandparents",
  "Great-Grandparents",
  "2nd Great-Grandparents",
  "3rd Great-Grandparents",
];

export function getGenerationName(generation: number): string {
  return GENERATION_NAMES[generation] || `Generation ${generation + 1}`;
}

/**
 * Relationship label from the root person, based on the path of parent
 * positions taken to reach the member (0 = father-side, 1 = mother-side).
 */
function labelForPath(path: number[], rootLabel: string): string {
  const prefix = rootLabel === "Spouse" ? "Spouse's " : "";
  if (path.length === 0) return rootLabel;

  const isFather = path[path.length - 1] === 0;
  const role = isFather ? "Father" : "Mother";

  if (path.length === 1) return `${prefix}${role}`;

  const side = path[0] === 0 ? "Paternal" : "Maternal";
  const grandRole = isFather ? "Grandfather" : "Grandmother";
  if (path.length === 2) return `${prefix}${side} ${grandRole}`;

  const greats = path.length - 2;
  const greatPrefix =
    greats === 1 ? "Great-" : `${greats === 2 ? "2nd" : `${greats}th`} Great-`;
  return `${prefix}${greatPrefix}${grandRole}`;
}

const isPlaceholderName = (name: string): boolean =>
  /your name|father's name|mother's name|spouse name/i.test(name);

/**
 * Flatten the family tree into guided-entry order: breadth-first by
 * generation, father-side before mother-side, your line before your spouse's.
 */
export function buildGuidedEntryList(
  familyTree: FamilyMember[]
): GuidedEntryItem[] {
  const items: GuidedEntryItem[] = [];
  const visited = new Set<string>();

  interface QueueEntry {
    member: FamilyMember;
    path: number[];
    rootLabel: string;
    childLabel: string | null;
  }

  let queue: QueueEntry[] = familyTree
    .filter(Boolean)
    .map((root, index) => ({
      member: root,
      path: [],
      rootLabel: index === 0 ? "You" : "Spouse",
      childLabel: null,
    }));

  while (queue.length > 0) {
    const nextQueue: QueueEntry[] = [];

    for (const entry of queue) {
      const { member, path, rootLabel, childLabel } = entry;
      if (!member || visited.has(member.id)) continue;
      visited.add(member.id);

      const relationshipLabel = labelForPath(path, rootLabel);
      items.push({
        member,
        relationshipLabel,
        parentOfLabel: childLabel,
        generation: path.length,
        status: member.editStatus || calculateEditStatus(member),
      });

      const displayName =
        member.name && !isPlaceholderName(member.name)
          ? member.name
          : relationshipLabel;

      getParents(member).forEach((parent, parentIndex) => {
        if (!parent) return;
        nextQueue.push({
          member: parent,
          path: [...path, parentIndex],
          rootLabel,
          childLabel: displayName,
        });
      });
    }

    queue = nextQueue;
  }

  return items;
}

/** Index of the first item after `fromIndex` that isn't complete (wraps). */
export function findNextIncompleteIndex(
  items: GuidedEntryItem[],
  fromIndex: number
): number {
  if (items.length === 0) return -1;
  for (let offset = 1; offset <= items.length; offset++) {
    const index = (fromIndex + offset) % items.length;
    if (items[index].status !== "complete") return index;
  }
  return -1;
}
